import React from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import TeamLogo from "./TeamLogo";
import { OddsButton } from "./sb";
import { hasStartedPregame } from "@/lib/sportsbookPriority";

/* GameMarketRow — the dense board row (design SBXGameRow.dc.html, README §2.2).
   Two team rows (away then home) over a status line, with Spread / Total / Moneyline
   cells aligned to the league-header grid. Desktop uses 112px columns + `row` odds
   buttons; mobile uses 64px columns + `rowM`. Prices are OddsButton so selection /
   lock / movement behave exactly like the rest of the book.

   Props:
     event      the /events row
     onPick(event, outcome)   add/remove a pick
     isPicked(eventId, outcomeId) -> bool
     moveFor(outcomeId) -> "up" | "down" | null   price-move arrow
     mobile     render the compact mobile variant
     showMeta   show the status line (hidden inside the event "Game lines" card)
     sgp        show the SGP tag on the status line
*/

const signed = (line) => {
  const n = Number(line);
  if (!Number.isFinite(n)) return line ?? "";
  return n > 0 ? `+${n}` : `${n}`;
};

// The book's abbreviation embedded in a moneyline label, e.g. "TEX Rangers".
function labelAbbr(o) {
  const m = /^([A-Z0-9]{2,4})\s/.exec((o?.label || "").trim());
  return m ? m[1] : "";
}

function fmtStart(iso) {
  if (!iso) return "Upcoming";
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
  } catch { return "Upcoming"; }
}

function TeamRow({ event, side, name, rank, score, scoreMuted, cells, mobile, onPick, isPicked, moveFor, locked }) {
  const cs = mobile ? 22 : 28;
  const grid = mobile ? "minmax(0,1fr) repeat(3,64px)" : "minmax(0,1fr) repeat(3,112px)";
  const variant = mobile ? "rowM" : "row";
  const cell = (o, line) =>
    o ? (
      <OddsButton
        testid={`odds-${event.id}-${o.id}`}
        line={line || ""}
        price={o.odds}
        variant={variant}
        selected={isPicked(event.id, o.id)}
        locked={locked || o.suspended === true || o.active === false}
        move={moveFor ? moveFor(o.id) : null}
        onClick={(e) => { if (e) { e.preventDefault?.(); e.stopPropagation?.(); } onPick(event, o); }}
      />
    ) : (
      <OddsButton na variant={variant} />
    );
  return (
    <div style={{ display: "grid", gridTemplateColumns: grid, columnGap: mobile ? 5 : 8, alignItems: "center" }}>
      <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: mobile ? 8 : 10, paddingRight: mobile ? 4 : 12 }}>
        <TeamLogo name={name} labelAbbr={labelAbbr(cells.ml)} league={event.league} sport={event.sport} size={cs} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 6, whiteSpace: "nowrap", overflow: "hidden" }}>
          {!mobile && rank ? <span style={{ flex: "none", font: "700 11px Manrope", color: "#71717a" }}>{rank}</span> : null}
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", font: `600 ${mobile ? 14 : 15}px Outfit`, color: "#fff" }}>{name}</span>
        </div>
        {score != null && (
          <span style={{ flex: "none", minWidth: 22, textAlign: "right", font: `800 ${mobile ? 16 : 18}px/1 Manrope`, letterSpacing: "-.02em", color: scoreMuted ? "#8A8A93" : "#fff", fontVariantNumeric: "tabular-nums" }}>{score}</span>
        )}
      </div>
      {cell(cells.spread, cells.spread ? signed(cells.spread.line) : null)}
      {cell(cells.total, cells.total ? `${side === "away" ? "O" : "U"} ${cells.total.line}` : null)}
      {cell(cells.ml, null)}
    </div>
  );
}

export default function GameMarketRow({ event, onPick, isPicked, moveFor, mobile = false, showMeta = true, sgp = false, navigable = false }) {
  const outs = event.outcomes || [];
  const pick = (side) => ({
    ml: outs.find((o) => o.market === "moneyline" && o.side === side),
    spread: outs.find((o) => o.market === "spread" && o.side === side),
    total: outs.find((o) => o.market === "total" && o.side === (side === "away" ? "over" : "under")),
  });
  const awayCells = pick("away");
  const homeCells = pick("home");
  const isLive = event.status === "live";
  // locked = not bettable: closed/settled OR a pre-game game that has already kicked off (not live)
  const locked = !(event.status === "open" || event.status === "live") || hasStartedPregame(event);
  const liveScore = isLive && event.gs_home_score != null;
  const aScore = liveScore ? event.gs_away_score : null;
  const hScore = liveScore ? event.gs_home_score : null;
  // "Market suspended" reflects ONLY the main-line cells this row actually renders (moneyline/spread/
  // total). Player props and feed-dropped/alt-line outcomes carry their own suspension flags and must
  // NOT flip the row label — a game with live main lines but suspended props is NOT a suspended market.
  const renderedCells = [awayCells.ml, awayCells.spread, awayCells.total, homeCells.ml, homeCells.spread, homeCells.total].filter(Boolean);
  const anySusp = renderedCells.length > 0 && renderedCells.some((o) => o.suspended === true || o.active === false);

  const body = (
    <div style={{ display: "flex", flexDirection: "column", gap: mobile ? 8 : 10, padding: mobile ? "12px 12px 14px" : "14px 20px 16px" }} data-testid={`event-${event.id}`}>
      {showMeta && (
        <div style={{ height: 22, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {isLive ? (
            <>
              <span data-testid={`live-badge-${event.id}`} style={{ flex: "none", height: 20, padding: "0 7px", borderRadius: 6, background: "rgba(239,68,68,.12)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.32)", display: "flex", alignItems: "center", gap: 5, font: "800 10px Outfit", letterSpacing: ".14em", color: "#EF4444" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#EF4444", flex: "none", animation: "pnrPulse 1.6s ease-out infinite" }} />LIVE
              </span>
              {event.gs_period && <span style={{ flex: "none", font: "700 12px Manrope", color: "#E4E4E7", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{event.gs_period}</span>}
            </>
          ) : (
            <span style={{ flex: "none", display: "flex", alignItems: "center", gap: 6, font: "600 12px Outfit", color: "#a1a1aa", whiteSpace: "nowrap" }}>
              <span style={{ width: 12, height: 12, background: "#71717a", WebkitMask: "url(/icons/clock.svg) center/contain no-repeat", mask: "url(/icons/clock.svg) center/contain no-repeat" }} />{fmtStart(event.start_time)}
            </span>
          )}
          {!mobile && anySusp && (
            <span style={{ flex: "none", display: "flex", alignItems: "center", gap: 5, font: "600 11px Outfit", color: "#71717a", whiteSpace: "nowrap" }}>
              <span style={{ width: 11, height: 11, background: "#71717a", WebkitMask: "url(/icons/lock.svg) center/contain no-repeat", mask: "url(/icons/lock.svg) center/contain no-repeat" }} />Market suspended
            </span>
          )}
          <div style={{ marginLeft: "auto", flex: "none", display: "flex", alignItems: "center", gap: 10 }}>
            {sgp && !mobile && (
              <span title="Same Game Parlay available" style={{ height: 18, padding: "0 5px", borderRadius: 5, boxShadow: "inset 0 0 0 1px #2F2F35", display: "flex", alignItems: "center", font: "800 9px Outfit", letterSpacing: ".12em", color: "#a1a1aa" }}>SGP</span>
            )}
            {/* whole row is clickable (navigable) — just a subtle affordance, no markets-count button */}
            {navigable && <Icon name="chevron-right" size={13} className="text-[#52525b]" />}
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <TeamRow event={event} side="away" name={event.away_team} rank={event.away_rank} score={aScore}
          scoreMuted={aScore != null && hScore != null && Number(aScore) < Number(hScore)} cells={awayCells}
          mobile={mobile} onPick={onPick} isPicked={isPicked} moveFor={moveFor} locked={locked} />
        <TeamRow event={event} side="home" name={event.home_team} rank={event.home_rank} score={hScore}
          scoreMuted={hScore != null && aScore != null && Number(hScore) < Number(aScore)} cells={homeCells}
          mobile={mobile} onPick={onPick} isPicked={isPicked} moveFor={moveFor} locked={locked} />
      </div>
    </div>
  );

  // Whole-row navigation: on the board the entire matchup is a link to its event page; the odds buttons
  // stopPropagation so tapping a price still just adds to the slip. (In EventDetail's game-lines card we
  // pass navigable={false} — you're already on the page.)
  if (navigable) {
    return (
      <Link to={`/event/${event.id}`} data-testid={`matchup-link-${event.id}`}
        style={{ display: "block", color: "inherit", textDecoration: "none", cursor: "pointer" }}>
        {body}
      </Link>
    );
  }
  return body;
}
